import React, { useEffect, useState } from "react";
import { ThemeContext } from "~/contexts/themeContextValue";
import type { Theme } from "~/contexts/useTheme";

/**
 * ThemeProvider wraps the app and manages light/dark mode state.
 * Persists preference to localStorage as "divide_ttrpg_theme".
 * Manages the "dark" class on the <html> element.
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Get persisted preference
    const stored = localStorage.getItem("divide_ttrpg_theme");
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    // Fall back to system preference
    if (typeof window !== "undefined") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      return prefersDark ? "dark" : "light";
    }

    return "light";
  });

  // Update <html> class when theme changes
  useEffect(() => {
    const html = document.documentElement;
    if (theme === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
    localStorage.setItem("divide_ttrpg_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState((prev) => (prev === "light" ? "dark" : "light"));
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
